import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";

import {
  readCmmdForgeRuntimeListSnapshot,
  type CloudProjectCatalogRow,
  useCmmdForgeRuntimeListSnapshot,
} from "../cmmd/cmmdForgeRuntimeListResource";
import {
  cmmdRuntimeInventoryResource,
  useCmmdRuntimeInventorySnapshot,
} from "../cmmd/cmmdRuntimeInventoryResource";
import {
  type CloudRuntimeProjectHandoff,
  useCloudRuntimeProjectHandoffStore,
} from "../components/sidebar/cloudRuntimeProjectHandoffStore";
import { findCloudRowForEnvironment } from "../routes/cloudRouteCatalogRow";
import { type AppState, selectProjectByRef, selectThreadByRef, useStore } from "../store";

interface ThreadInventorySnapshot {
  readonly runtimes: readonly {
    readonly runtime: { readonly id: string };
    readonly projects: readonly {
      readonly projectId: string;
      readonly githubRepoFullName: string | null;
      readonly workspaceRoot?: string | null;
      readonly threads: readonly { readonly threadId: string }[];
    }[];
  }[];
}

/** Every Registration row on the Environment the anchor row belongs to. */
function rowsOnResolvedEnvironment(
  anchor: CloudProjectCatalogRow,
  rows: readonly CloudProjectCatalogRow[],
): readonly CloudProjectCatalogRow[] {
  if (anchor.cmmdEnvironmentId !== null) {
    return rows.filter((row) => row.cmmdEnvironmentId === anchor.cmmdEnvironmentId);
  }
  if (anchor.runtimeId !== null) {
    return rows.filter((row) => row.runtimeId === anchor.runtimeId);
  }
  return [anchor];
}

/**
 * A directory as both sides of a comparison should spell it, or null when it names nothing.
 * The inventory and a Registration record the same path with different trimming.
 */
function normalizeDirectory(directory: string | null | undefined): string | null {
  const trimmed = directory?.trim() ?? "";
  if (trimmed.length === 0) return null;
  // The filesystem root is a directory too, and stripping its only slash would erase it.
  return trimmed.replace(/\/+$/, "") || "/";
}

/** Whether a Registration row names the repository behind a canonical key. */
function rowNamesRepository(row: CloudProjectCatalogRow, canonicalKey: string): boolean {
  const repository = row.githubRepoFullName?.toLowerCase();
  if (!repository) return false;
  const normalizedKey = canonicalKey.toLowerCase().replace(/\.git$/, "");
  return normalizedKey === repository || normalizedKey.endsWith(`/${repository}`);
}

type InventoryCheckout = ThreadInventorySnapshot["runtimes"][number]["projects"][number];

/** The one inventory Checkout that owns the thread, or null when none or several do. */
function findInventoryThreadCheckout(
  inventory: ThreadInventorySnapshot | null,
  runtimeId: string | null,
  projectId: string | null,
  threadId: string,
): InventoryCheckout | null {
  if (!runtimeId) return null;
  const runtime = inventory?.runtimes.find((candidate) => candidate.runtime.id === runtimeId);
  const projects =
    runtime?.projects.filter(
      (candidate) =>
        (projectId === null || String(candidate.projectId) === projectId) &&
        candidate.threads.some((thread) => thread.threadId === threadId),
    ) ?? [];
  return projects.length === 1 ? (projects[0] ?? null) : null;
}

/**
 * Resolve the Registration for a retained Checkout without guessing by title.
 * A stored Registration wins. Legacy records may recover only when one row on
 * the same durable Environment names their exact repository identity.
 */
export function resolveThreadCmmdProjectId(
  threadRef: ScopedThreadRef,
  state: AppState,
  rows: readonly CloudProjectCatalogRow[],
  handoffs: readonly CloudRuntimeProjectHandoff[],
  inventory: ThreadInventorySnapshot | null = null,
): number | null {
  const thread = selectThreadByRef(state, threadRef);
  const project = thread
    ? selectProjectByRef(state, scopeProjectRef(thread.environmentId, thread.projectId))
    : undefined;
  const registeredProjectId = project?.cmmdRegistration?.cmmdProjectId;
  if (registeredProjectId !== undefined) return registeredProjectId;

  const resolvedEnvironment = findCloudRowForEnvironment(threadRef.environmentId, rows, handoffs);
  if (!resolvedEnvironment) return null;
  const checkout = findInventoryThreadCheckout(
    inventory,
    resolvedEnvironment.runtimeId,
    thread ? String(thread.projectId) : null,
    String(threadRef.threadId),
  );
  const candidates = rowsOnResolvedEnvironment(resolvedEnvironment.row, rows);
  const canonicalKey =
    project?.repositoryIdentity?.canonicalKey ?? checkout?.githubRepoFullName ?? null;
  // A Checkout with no GitHub repository is named by its directory on the same Environment.
  const directory = canonicalKey ? null : normalizeDirectory(checkout?.workspaceRoot);
  const matches = candidates.filter((row) =>
    canonicalKey
      ? rowNamesRepository(row, canonicalKey)
      : directory !== null && normalizeDirectory(row.workspaceRoot) === directory,
  );
  return matches.length === 1 ? (matches[0]?.cmmdProjectId ?? null) : null;
}

/** Reads the durable CMMD project identity for the exact Checkout that owns a thread. */
export function readThreadCmmdProjectId(threadRef: ScopedThreadRef): number | null {
  return resolveThreadCmmdProjectId(
    threadRef,
    useStore.getState(),
    readCmmdForgeRuntimeListSnapshot().rows,
    Object.values(useCloudRuntimeProjectHandoffStore.getState().byRuntimeId),
    cmmdRuntimeInventoryResource.read().inventory,
  );
}

/** Subscribes UI to the durable Checkout identity that owns a thread. */
export function useThreadCmmdProjectId(threadRef: ScopedThreadRef): number | null {
  const rows = useCmmdForgeRuntimeListSnapshot().rows;
  const inventory = useCmmdRuntimeInventorySnapshot().inventory;
  const handoffsByRuntimeId = useCloudRuntimeProjectHandoffStore((state) => state.byRuntimeId);
  const handoffs = Object.values(handoffsByRuntimeId);
  return useStore((state) =>
    resolveThreadCmmdProjectId(threadRef, state, rows, handoffs, inventory),
  );
}
